import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Box, CircularProgress, Typography } from '@mui/material';
import { PdfToolbar, PdfSidebar, PdfAnnotatePanel, PdfCanvas, PdfPage, usePdfView } from '../../../components/PdfToolbar';
import './PayReceiveFormat2PrintPage.css';

// A5 (portrait) Bank / Cash Payment & Receipt voucher — format 2 (narration style, single amount).
// Data comes from GET /api/get-payReceive-pdf-data (master + billwise + company).

// Header printed in the title bar, chosen by the voucher's BookName.
const VOUCHER_TITLES = {
    BKP: 'BANK PAYMENT VOUCHER',
    BKR: 'BANK RECEIPT VOUCHER',
    CBP: 'CASH PAYMENT VOUCHER',
    CBR: 'CASH RECEIPT VOUCHER',
};

const ONES = ['', 'ONE', 'TWO', 'THREE', 'FOUR', 'FIVE', 'SIX', 'SEVEN', 'EIGHT', 'NINE', 'TEN', 'ELEVEN', 'TWELVE', 'THIRTEEN', 'FOURTEEN', 'FIFTEEN', 'SIXTEEN', 'SEVENTEEN', 'EIGHTEEN', 'NINETEEN'];
const TENS = ['', '', 'TWENTY', 'THIRTY', 'FORTY', 'FIFTY', 'SIXTY', 'SEVENTY', 'EIGHTY', 'NINETY'];

const below100 = (n) => (n < 20 ? ONES[n] : `${TENS[Math.floor(n / 10)]}${n % 10 ? ` ${ONES[n % 10]}` : ''}`);
const below1000 = (n) => {
    const h = Math.floor(n / 100);
    const r = n % 100;
    return `${h ? `${ONES[h]} HUNDRED` : ''}${h && r ? ' ' : ''}${r ? below100(r) : ''}`;
};
// Indian numbering (Crore / Lakh / Thousand)
const intToWords = (num) => {
    if (num === 0) return 'ZERO';
    const parts = [];
    const crore = Math.floor(num / 10000000);
    const lakh = Math.floor((num % 10000000) / 100000);
    const thousand = Math.floor((num % 100000) / 1000);
    const rest = num % 1000;
    if (crore) parts.push(`${below100(crore)} CRORE`);
    if (lakh) parts.push(`${below100(lakh)} LAKH`);
    if (thousand) parts.push(`${below100(thousand)} THOUSAND`);
    if (rest) parts.push(below1000(rest));
    return parts.join(' ');
};
const amountInWords = (value) => {
    const total = Math.round((Number(value) || 0) * 100);
    const rupees = Math.floor(total / 100);
    const paise = total % 100;
    let words = `RUPEES : ${intToWords(rupees)}`;
    if (paise) words += ` AND ${below100(paise)} PAISE`;
    return `${words} ONLY`;
};

const toNum = (v) => (v === null || v === undefined || v === '' ? 0 : Number(v) || 0);
const formatAmt = (v) => {
    const n = toNum(v);
    return n === 0 ? '' : n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
// "2026-10-31" / "2026-10-31 00:00:00" -> "31-10-26" (string split, no Date parsing, so no timezone shift)
const formatDate = (value) => {
    if (!value) return '';
    const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[3]}-${m[2]}-${m[1].slice(2)}` : String(value);
};

export default function PayReceiveFormat2PrintPage({ connectionString }) {
    const { receiptId } = useParams();
    const [searchParams] = useSearchParams();
    const companyId = searchParams.get('companyId');
    const type = searchParams.get('type');

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        axios
            .get(`${connectionString}/api/get-payReceive-pdf-data`, { params: { receiptId, companyId, type } })
            .then((res) => { if (!cancelled) setData(res.data); })
            .catch((err) => {
                console.error('Failed to load receipt/payment for print:', err);
                if (!cancelled) setError('Failed to load receipt/payment.');
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [receiptId, companyId, type, connectionString]);

    const handlePrint = () => window.print();

    // Zoom / fit / page navigation / annotate / download state for the toolbar (hook must run before any early return).
    const view = usePdfView(1);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
                <CircularProgress />
            </Box>
        );
    }

    if (error || !data?.master) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
                <Typography color="error">{error || 'Receipt/Payment not found.'}</Typography>
            </Box>
        );
    }

    const { master, company } = data;
    const bookName = String(master.BookName || '').toUpperCase();
    const title = VOUCHER_TITLES[bookName] || '';
    const isPayment = bookName === 'BKP' || bookName === 'CBP';
    const isCash = bookName === 'CBP' || bookName === 'CBR';
    const totalAmount = toNum(master.PaidAmt) || toNum(master.Amount);

    // Mode of payment shown in the narration line.
    const mode = isCash ? 'CASH' : (master.ChequeNo ? `CHEQUE NO. ${master.ChequeNo}` : 'BANK');
    const narration = `WE HAVE ${isPayment ? 'DEBITED' : 'CREDITED'} YOUR ACCOUNT SUM OF RS. ${formatAmt(totalAmount)} BY ${mode}`;

    const partyAddressLines = [
        master.PartyAddress,
        [master.PartyArea, master.PartyCity].filter(Boolean).join(', '),
        [master.PartyState, master.PartyPincode].filter(Boolean).join(' - '),
    ].filter((v) => v && String(v).trim());

    return (
        <>
            <PdfToolbar pageCount={1} view={view} onPrint={handlePrint} downloadFilename={`${title || 'Voucher'}-${master.ReceiptNumber || receiptId}.pdf`.replace(/[\\/:*?"<>|]/g, '-')} />
            <PdfSidebar view={view} />
            <PdfAnnotatePanel view={view} />

            <PdfCanvas view={view} className="pr2-canvas">
                <PdfPage view={view} index={0} className="pr2-sheet">
                    <div className="pr2-header">
                        <div className="pr2-company-name">{company?.CompanyName || ''}</div>
                        {company?.Address && <div className="pr2-company-line">{company.Address}</div>}
                        {company?.MobileNo && <div className="pr2-company-line">MO. : {company.MobileNo}</div>}
                        {company?.EmailId && <div className="pr2-company-line">E-MAIL : {company.EmailId}</div>}
                    </div>

                    <div className="pr2-box">
                        <div className="pr2-title">{title}</div>

                        <div className="pr2-to-row">
                            <div className="pr2-to-left">
                                <div className="pr2-to-label">To,</div>
                                <div className="pr2-to-name">{master.PartyName || ''}</div>
                                <div className="pr2-to-address">
                                    {partyAddressLines.map((line, i) => <div key={i}>{line}</div>)}
                                </div>
                            </div>
                            <div className="pr2-to-right">
                                <div><span className="pr2-k">Receipt No</span><span className="pr2-c">:</span><span>{master.ReceiptNumber || ''}</span></div>
                                <div><span className="pr2-k">Date</span><span className="pr2-c">:</span><span>{formatDate(master.ReceiptDate)}</span></div>
                            </div>
                        </div>

                        <table className="pr2-table">
                            <colgroup>
                                <col />
                                <col className="pr2-col-amt" />
                            </colgroup>
                            <thead>
                                <tr>
                                    <th>Narration</th>
                                    <th>Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td className="pr2-narration">
                                        <div>{narration}</div>
                                        {master.Remark && <div className="pr2-remark">{master.Remark}</div>}
                                    </td>
                                    <td className="pr2-r">{formatAmt(totalAmount)}</td>
                                </tr>
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td className="pr2-words">{amountInWords(totalAmount)}</td>
                                    <td className="pr2-r">{formatAmt(totalAmount)}</td>
                                </tr>
                            </tfoot>
                        </table>

                        <div className="pr2-footer">
                            <span>RECEIVER' S SIGNATURE</span>
                            <span>(Authorised Signatory)</span>
                        </div>
                    </div>
                </PdfPage>
            </PdfCanvas>
        </>
    );
}
